import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Divider, Form } from "semantic-ui-react";
import Loader from "../components/Loader";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import FormSelect from "../components/FormSelect";
import FormInput from "../components/FormInput";
import FormRadio from "../components/FormRadio";
import SaveButton from "../components/SaveButton";
import TourStore from "~/stores/TourStore";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import ModalError from "../../../components/element/ModalError";
import "../styles/TourSettings.less";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import StickyMenu from "~/components/StickyMenu";
import ToastMessageStore from "~/stores/ToastMessageStore";

class Settings extends Component {
  constructor(props) {
    super(props);
    document.title = "Tour - Settings";
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  save() {
    const { t, tourStore, toastMessageStore } = this.props;

    tourStore.save().then(() => {
      toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
    });
  }

  render() {
    const { tourStore, t, tourLayoutContentContainerRef } = this.props;
    const { isLoading, isSaving } = tourStore;

    return (
      <>
        <Loader active={isLoading} page content={t("loading")} />
        <ModalUnsavedConfirmation
          when={tourStore.hasUnsavedData}
          onSave={() => this.save()}
          onDiscard={() => tourStore.reset()}
        />
        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <ModalError nestStore={tourStore} />

        <StickyMenu
          contextRef={tourLayoutContentContainerRef}
          headerText={t("clientPanelMisc:settings")}
          button={<SaveButton disabled={!tourStore.hasUnsavedData} onClick={() => this.save()} />}
        />

        <Form className="tour-settings-form">
          <h4>{t("clientPanelMisc:mapsOtherToursSendToFriend")}</h4>
          <div className="tour-settings-row">
            <FormSelect
              label={t("clientPanelMisc:mapLink")}
              defaultValue={tourStore.tourLoaded ? tourStore.tour.customerDefaults.showMap : true}
              options={[
                { key: "true", value: "true", text: t("show") },
                { key: "false", value: "false", text: t("hide") },
              ]}
              name="tour.showMap"
              dataStore={tourStore}
            />

            <FormSelect
              label={t("clientPanelMisc:otherToursLink")}
              defaultValue={
                tourStore.tour && tourStore.tour.customerDefaults
                  ? tourStore.tour.customerDefaults.hasShowOtherTours
                  : true
              }
              options={[
                { key: "true", value: "true", text: t("show") },
                { key: "false", value: "false", text: t("hide") },
              ]}
              name="tour.hasShowOtherTours"
              dataStore={tourStore}
            />

            <FormSelect
              label={t("clientPanelMisc:sendFriendLink")}
              defaultValue={
                tourStore.tour && tourStore.tour.customerDefaults
                  ? tourStore.tour.customerDefaults.hasSendToFriend
                  : true
              }
              options={[
                { key: "true", value: "true", text: t("show") },
                { key: "false", value: "false", text: t("hide") },
              ]}
              name="tour.hasSendToFriend"
              dataStore={tourStore}
            />
          </div>
          <Divider className="tour-settings-divider" />

          <h4>{t("clientPanelMisc:flyers")}</h4>
          <div className="tour-settings-row">
            <FormSelect
              label={t("clientPanelMisc:flyerLink")}
              defaultValue={
                tourStore.tour && tourStore.tour.customerDefaults
                  ? tourStore.tour.customerDefaults.showPrintableFlyer
                  : true
              }
              options={[
                { key: "true", value: "true", text: t("show") },
                { key: "false", value: "false", text: t("hide") },
              ]}
              name="tour.showPrintableFlyer"
              dataStore={tourStore}
            />
            <FormInput
              label={t("clientPanelMisc:brandedFlyerUrl")}
              dataStore={tourStore}
              name="tour.customFlyerUrlBranded"
            />
            <FormInput
              label={t("clientPanelMisc:unbrandedFlyerUrl")}
              dataStore={tourStore}
              name="tour.customFlyerUrlUnbranded"
            />
          </div>
          <Divider className="tour-settings-divider" />

          <h4>{t("clientPanelInfo:neighborhood")}</h4>
          <div className="tour-settings-row">
            <FormSelect
              label={t("clientPanelInfo:neighborhoodInfoLink")}
              defaultValue={
                tourStore.tour && tourStore.tour.customerDefaults
                  ? tourStore.tour.customerDefaults.showNeighborhood
                  : true
              }
              options={[
                { key: "true", value: "true", text: t("show") },
                { key: "false", value: "false", text: t("hide") },
              ]}
              name="tour.showNeighborhood"
              dataStore={tourStore}
            />
            <FormInput
              label={t("clientPanelInfo:neighborhoodLabel")}
              dataStore={tourStore}
              name="tour.customNeighborhoodLabel"
            />
            <div className="tour-settings-radio-wrap">
              <FormInput
                label={t("clientPanelInfo:customNeighborhoodUrl")}
                dataStore={tourStore}
                name="tour.customNeighborhoodUrl"
              />
              <FormRadio
                label={t("openLinkNewWindow")}
                value
                dataStore={tourStore}
                name="tour.customNeighborhoodOpenInNewWindow"
              />
              <FormRadio
                label={t("openLinkPopupBox")}
                value={false}
                dataStore={tourStore}
                name="tour.customNeighborhoodOpenInNewWindow"
              />
            </div>
          </div>
          <Divider className="tour-settings-divider" />

          <h4>{t("clientPanelMisc:customLink1")}</h4>
          <div className="tour-settings-row">
            <FormInput label={t("clientPanelMisc:link1Title")} dataStore={tourStore} name="tour.customLink1Label" />
            <FormInput label={t("brandedUrl")} dataStore={tourStore} name="tour.customLink1UrlBranded" />
            <FormInput label={t("unbrandedUrl")} dataStore={tourStore} name="tour.customLink1UrlUnbranded" />
            <div className="tour-settings-radio-wrap">
              <FormRadio
                label={t("openLinkNewWindow")}
                value
                dataStore={tourStore}
                name="tour.customLink1OpenInNewWindow"
              />
              <FormRadio
                label={t("openLinkPopupBox")}
                value={false}
                dataStore={tourStore}
                name="tour.customLink1OpenInNewWindow"
              />
            </div>
          </div>
          <Divider className="tour-settings-divider" />

          <h4>{t("clientPanelMisc:customLink2")}</h4>
          <div className="tour-settings-row">
            <FormInput label={t("clientPanelMisc:link2Title")} dataStore={tourStore} name="tour.customLink2Label" />
            <FormInput label={t("brandedUrl")} dataStore={tourStore} name="tour.customLink2UrlBranded" />
            <FormInput label={t("unbrandedUrl")} dataStore={tourStore} name="tour.customLink2UrlUnbranded" />
            <div className="tour-settings-radio-wrap">
              <FormRadio
                label={t("openLinkNewWindow")}
                value
                dataStore={tourStore}
                name="tour.customLink2OpenInNewWindow"
              />
              <FormRadio
                label={t("openLinkPopupBox")}
                value={false}
                dataStore={tourStore}
                name="tour.customLink2OpenInNewWindow"
              />
            </div>
          </div>
          <Divider className="tour-settings-divider" />

          <h4>{t("3DTour")}</h4>
          <div className="tour-settings-row">
            <FormInput label={t("clientPanelInfo:3dTourTitle")} dataStore={tourStore} name="tour.matterportLabel" />
            <FormInput
              label={t("clientPanelInfo:3dTourBrandedUrl")}
              dataStore={tourStore}
              name="tour.matterportUrlBranded"
            />
            <FormInput
              label={t("clientPanelInfo:3dTourUnbrandedUrl")}
              dataStore={tourStore}
              name="tour.matterportUrlUnbranded"
            />
          </div>
        </Form>
      </>
    );
  }
}

Settings.propTypes = {
  tourStore: PropTypes.instanceOf(TourStore),
  t: PropTypes.func.isRequired,
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(TourLayoutWrapper(observer(Settings), TourNav.SETTINGS));
